
import { Component, Vue, Prop } from 'vue-property-decorator';
import { checkRole } from '@/modules/authentication/helpers/checkRole';
import { Roles } from '@/modules/authentication/models/rolesEnum';
import * as ResultFileService from '@/modules/analysis/services/resultFileService';
import Dialog, { DialogData } from '@/components/dialog/Dialog.vue';
import { ResultFile } from '@/modules/analysis/models/analysisRequest';
import { ErrorEnum } from '@/models/errorEnum';

@Component({
  components: { Dialog }
})
export default class AboutResults extends Vue {
  @Prop({ required: true })
  protected requestId?: string;
  @Prop()
  protected resultFiles?: ResultFile[];
  protected inputFiles: File[] = [];
  protected dialogError: DialogData = {
    isOpen: false,
    title: '',
    content: ''
  };
  protected dialogConfirm: { data: DialogData; fileId?: string; callBack?: (id: string) => void } = {
    data: {
      isOpen: false,
      title: '',
      content: ''
    },
    fileId: undefined,
    callBack: undefined
  };

  protected triggerConfirm(file: ResultFile, callBack: (id: string) => void): void {
    if (callBack === this.publish) {
      this.dialogConfirm.data.title = this.$i18n.t('analysis.AboutResultFile.publishDialog.title').toString();
      this.dialogConfirm.data.content = this.$i18n
        .t('analysis.AboutResultFile.publishDialog.content', {
          fileName: file.fileName
        })
        .toString();
    } else if (callBack === this.remove) {
      this.dialogConfirm.data.title = this.$i18n.t('analysis.AboutResultFile.deleteDialog.title').toString();
      this.dialogConfirm.data.content = this.$i18n
        .t('analysis.AboutResultFile.deleteDialog.content', {
          fileName: file.fileName
        })
        .toString();
    }
    this.dialogConfirm.callBack = callBack;
    this.dialogConfirm.fileId = file.id;
    this.dialogConfirm.data.isOpen = true;
  }

  /**
   * The user has input files after clicked into send button.
   */
  protected saveFiles(): void {
    for (const file of this.inputFiles) {
      if (
        !file.type.startsWith('text/csv') &&
        !file.type.startsWith('application/vnd.ms-excel') && // Excel Files 97-2003
        !file.type.startsWith('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') && // Excel Files 2007+
        !file.type.startsWith('application/pdf') // PDF
      ) {
        this.openAndSetDialogErrorContent(ErrorEnum.INVALID_FILE_EXTENSION);
        return;
      }
    }

    ResultFileService.create(this.inputFiles, this.requestId as string)
      .then(() => {
        this.inputFiles = [];
        this.$emit('refresh');
      })
      .catch((error) => {
        if (error?.response?.status === 400) {
          this.openAndSetDialogErrorContent(ErrorEnum.NO_FILE_PROVIDED);
        } else if (error?.response?.status === 404) {
          this.openAndSetDialogErrorContent(ErrorEnum.ANALYSIS_REQUEST_NOT_FOUND);
        } else if (error?.response?.status === 409) {
          this.openAndSetDialogErrorContent(ErrorEnum.FILE_ALREADY_EXISTS);
        } else {
          this.openAndSetDialogErrorContent(ErrorEnum.INTERNAL_SERVER_ERROR);
        }
      });
  }

  protected download(file: ResultFile): void {
    if (file.id) {
      ResultFileService.download(file.id)
        .then((res) => {
          const blob = new Blob([res.data], { type: file.mimetype });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = file.fileName;
          link.click();
          URL.revokeObjectURL(link.href);
        })
        .catch((message) => {
          this.handleServiceError(message);
        });
    }
  }

  protected publish(fileId: string): void {
    if (fileId) {
      ResultFileService.publish(fileId)
        .then(() => {
          this.$emit('refresh');
        })
        .catch((message) => {
          this.handleServiceError(message);
        })
        .finally(() => {
          this.dialogConfirm.data.isOpen = false;
        });
    }
  }

  protected remove(fileId: string): void {
    if (fileId) {
      ResultFileService.destroy(fileId)
        .then(() => {
          this.$emit('refresh');
        })
        .catch((message) => {
          this.handleServiceError(message);
        })
        .finally(() => (this.dialogConfirm.data.isOpen = false));
    }
  }

  protected handleServiceError(message: { response: { status: number; data: { error: Error } } }): void {
    let errorContent = ErrorEnum.INTERNAL_SERVER_ERROR.toString();
    if (message?.response?.status < 200 || message?.response?.status > 299) {
      const errorMessage = Object.keys(ErrorEnum).find((key) => key === message?.response?.data?.error?.message);
      if (errorMessage) {
        errorContent = errorMessage;
      }
    }
    this.openAndSetDialogErrorContent(errorContent);
  }

  protected openAndSetDialogErrorContent(code: string): void {
    this.dialogError.isOpen = true;
    if (code === ErrorEnum.INTERNAL_SERVER_ERROR) {
      this.dialogError.title = this.$i18n.t(`common.InternalError.fileError.${code}.title`).toString();
      this.dialogError.content = this.$i18n.t(`common.InternalError.fileError.${code}.content`).toString();
    } else {
      this.dialogError.title = this.$i18n.t(`analysis.AboutResultFile.fileError.${code}.title`).toString();
      this.dialogError.content = this.$i18n.t(`analysis.AboutResultFile.fileError.${code}.content`).toString();
    }
  }

  protected get isResultsUploadAuthorized(): boolean {
    return checkRole(Roles.results_upload);
  }

  protected get isResultsViewAuthorized(): boolean {
    return checkRole(Roles.results_view);
  }

  protected get isResultsPublishAuthorized(): boolean {
    return checkRole(Roles.results_publish);
  }

  protected get filenameMaxWidth(): string {
    const divHorPadding = 16 * 2;
    const actionsWidth = 140;

    if (this.$refs['toolbar'] instanceof Vue) {
      const element = (this.$refs['toolbar'] as Vue).$el as HTMLElement;

      return `calc(${element.offsetWidth}px - ${divHorPadding}px - ${actionsWidth}px)`;
    }
    return '200px';
  }

  protected get authorizedResultFiles(): ResultFile[] {
    return this.resultFiles?.filter((file) => {
      return this.isResultsUploadAuthorized || file.published;
    }) as ResultFile[];
  }
}
